import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import server from '../../../api/server';
import { refreshVOIData } from '../../../actions';
import Card from '../ui/Card';
import Button from '../ui/Button';

// Redesign of newadmin/VOIData.js — same refreshVOIData on mount, same
// /voipdf report download, same pass/fail result rendering.
function calcCrow(lat1, lon1, lat2, lon2) {
    var R = 6371; // km
    var dLat = toRad(lat2 - lat1);
    var dLon = toRad(lon2 - lon1);
    lat1 = toRad(lat1);
    lat2 = toRad(lat2);

    var a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

function toRad(value) {
    return (value * Math.PI) / 180;
}

const PASS_STYLE = { backgroundColor: '#D1FAE5', color: '#065F46' };
const FAIL_STYLE = { backgroundColor: '#FEE2E2', color: '#991B1B' };

const ResultChip = ({ passed, children, className = '' }) => (
    <div
        className={`rounded-xl px-4 py-2.5 text-sm font-semibold text-center capitalize ${className}`}
        style={passed ? PASS_STYLE : FAIL_STYLE}
    >
        {children}
    </div>
);

const DetailRow = ({ label, value }) => (
    <div className="flex items-start justify-between gap-4 py-2.5 border-b border-[#F0F1F5] last:border-b-0">
        <span className="text-sm font-semibold text-[#1F2937] capitalize">{label}</span>
        <span className="text-sm text-[#6B7280] text-right break-words min-w-0">{value}</span>
    </div>
);

const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'docs', label: 'Documents' },
    { key: 'face', label: 'Face' },
];

const VOIApplicantCard = ({ applicantId }) => {
    const VOI = useSelector((s) => s.admin.active_application.VOI);
    const user = useSelector((s) => s.user);
    const dispatch = useDispatch();

    const [menu, setMenu] = useState('overview');
    const [pdfDisabled, setPdfDisabled] = useState(false);

    const applicant = VOI.applicants[applicantId];

    useEffect(() => {
        dispatch(refreshVOIData(applicant.transaction_id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const data = applicant.data;
    const userDetails = data.user_details;
    const fullName = `${userDetails.first_name} ${userDetails.middle_name} ${userDetails.last_name}`;

    // Port of VOIData.downloadReport — same /voipdf request + blob download.
    const downloadReport = async (name) => {
        setPdfDisabled(true);
        const response = await server.post('/voipdf', {
            id: user.token,
            transaction_id: applicant.transaction_id,
        });

        function base64ToArrayBuffer(base64) {
            var bString = window.atob(base64);
            var bLength = bString.length;
            var bytes = new Uint8Array(bLength);
            for (var i = 0; i < bLength; i++) {
                bytes[i] = bString.charCodeAt(i);
            }
            return bytes;
        }

        var bufferArray = base64ToArrayBuffer(response.data.binary);
        var blobStore = new Blob([bufferArray], { type: 'application/pdf' });
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveOrOpenBlob(blobStore);
        } else {
            var url = window.URL.createObjectURL(blobStore);
            var link = document.createElement('a');
            document.body.appendChild(link);
            link.href = url;
            link.download = `${name}.pdf`;
            link.click();
            window.URL.revokeObjectURL(url);
            link.remove();
        }
        setPdfDisabled(false);
    };

    const renderUserDetails = () => {
        const details = [];
        Object.keys(userDetails).forEach((key) => {
            const tag = key.split('_').join(' ');
            if (typeof userDetails[key] == 'object' || userDetails[key] == null) {
                return;
            }
            if (key != 'address' && userDetails[key] != null) {
                details.push(<DetailRow key={key} label={tag} value={userDetails[key]} />);
            }
        });
        return details;
    };

    const renderBreakdown = () => {
        const features = data.likeness_features;
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {Object.keys(features).map((key) => (
                    <ResultChip key={key} passed={features[key] == 'Pass'}>
                        {key.split('_').join(' ')}
                    </ResultChip>
                ))}
            </div>
        );
    };

    const renderOverview = () => (
        <div>
            <h3 className="text-lg font-bold text-[#1F2937] mb-4">{fullName}</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-wide text-[#9CA3AF] mb-1">DOB</p>
                        <p className="text-sm text-[#1F2937]">{userDetails.date_of_birth}</p>
                    </div>
                    <div>
                        <p className="text-xs font-bold uppercase tracking-wide text-[#9CA3AF] mb-1">Number</p>
                        <p className="text-sm text-[#1F2937]">61 0415772392</p>
                    </div>
                    <div>
                        <p className="text-xs font-bold uppercase tracking-wide text-[#9CA3AF] mb-1">Address</p>
                        <p className="text-sm text-[#1F2937]">
                            {userDetails.address ? userDetails.address.full_address : 'Not Available'}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs font-bold uppercase tracking-wide text-[#9CA3AF] mb-1">Report</p>
                        <Button disabled={pdfDisabled} onClick={() => downloadReport(fullName)}>
                            {pdfDisabled ? 'Downloading…' : 'Download Report'}
                        </Button>
                    </div>
                </div>
                <div className="space-y-2">
                    <ResultChip passed={data.results.documents == 'PASSED'}>Documents</ResultChip>
                    <ResultChip passed={data.results.facematch == 'PASSED'}>Face</ResultChip>
                    <ResultChip passed={data.results.liveness == 'PASSED'}>Liveness</ResultChip>
                </div>
            </div>
        </div>
    );

    const renderDocs = () => {
        const documents = data.documents || [];
        return (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                    {documents.map((document, index) => (
                        <div key={index} className="mb-5">
                            <h4 className="text-sm font-bold text-[#1F2937] mb-3">Document {index + 1}</h4>
                            {document.front_document && (
                                <>
                                    <img
                                        src={document.front_document.url}
                                        className="w-full rounded-xl border border-[#E5E7EB]"
                                        alt={`Front of document ${index + 1}`}
                                    />
                                    <p className="text-center text-xs text-[#6B7280] mt-1">Front</p>
                                </>
                            )}
                            {document.back_document && (
                                <div>
                                    <img
                                        src={document.back_document.url}
                                        className="w-full rounded-xl border border-[#E5E7EB] mt-4"
                                        alt={`Back of document ${index + 1}`}
                                    />
                                    <p className="text-center text-xs text-[#6B7280] mt-1">Back</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-[#6B7280] pb-2 border-b border-[#F0F1F5]">
                        Document Info
                    </p>
                    {renderUserDetails()}
                    {data.spoof_result && (
                        <div className="mt-4 rounded-xl px-4 py-3 text-sm font-semibold text-center" style={PASS_STYLE}>
                            {data.spoof_result}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderFace = () => (
        <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="flex flex-col items-center">
                    <p className="text-sm text-[#6B7280] mb-2">Face from ID Document</p>
                    <img
                        className="rounded-xl border border-[#E5E7EB] object-cover"
                        style={{ width: '250px', height: '300px' }}
                        src={data.documents[0].face_photo.url}
                        alt="Face from ID document"
                    />
                </div>
                <div className="flex flex-col items-center">
                    <p className="text-sm text-[#6B7280] mb-2">Face from Liveness Video</p>
                    <img
                        className="rounded-xl border border-[#E5E7EB] object-cover"
                        style={{ width: '250px', height: '300px' }}
                        src={data.facematch_best_frame.url}
                        alt="Face from liveness video"
                    />
                </div>
            </div>
            <div className="mt-6">{renderBreakdown()}</div>
        </div>
    );

    const renderData = () => {
        const fieldDetails = data.data_provider.DATA_CONNECT.data.fieldDetails;
        if (fieldDetails == undefined) {
            return (
                <div>
                    <p className="text-sm text-[#6B7280]">No Data</p>
                </div>
            );
        }

        const rows = Object.keys(fieldDetails).map((key) => {
            const tag = key.match(/[A-Z][a-z]+/g).join(' ');
            return <DetailRow key={key} label={tag} value={fieldDetails[key]} />;
        });

        const dataDetails = data.device_info;
        if (dataDetails == undefined || dataDetails == null) {
            return (
                <div>
                    <p className="text-sm text-[#6B7280]">No Data</p>
                </div>
            );
        }

        const dataRows = Object.keys(dataDetails).map((key) => {
            let value = dataDetails[key];
            if (key == 'vpn') {
                value = value ? 'Yes' : 'No';
            }
            return <DetailRow key={key} label={key} value={value} />;
        });

        if (dataDetails.geolocation != null && dataDetails.ip_location != null) {
            const geolocations = dataDetails.geolocation.split(',');
            const ipLocation = dataDetails.ip_location.split(',');
            const distanceBetween = calcCrow(geolocations[0], geolocations[1], ipLocation[0], ipLocation[1]);
            dataRows.push(
                <DetailRow key="distance" label="Distance (IP vs Location)" value={`${distanceBetween.toFixed(2)}km`} />
            );
        }

        return (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-[#6B7280] pb-2 border-b border-[#F0F1F5]">
                        Data Matching
                    </p>
                    {rows}
                </div>
                <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-[#6B7280] pb-2 border-b border-[#F0F1F5]">
                        Device Information
                    </p>
                    {dataRows}
                </div>
            </div>
        );
    };

    const tabs = [...TABS];
    if ('data_provider' in data) {
        tabs.push({ key: 'data', label: 'Data' });
    }

    const overallPassed = data.results.overall == 'PASSED';

    return (
        <Card
            title={fullName}
            subtitle="Verification of Identity"
            actions={
                <div className="flex rounded-xl bg-[#F5F6FA] p-1 gap-1">
                    {tabs.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            onClick={() => setMenu(tab.key)}
                            className="rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors"
                            style={
                                menu === tab.key
                                    ? { backgroundColor: '#FFFFFF', color: '#5B3FE0', boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }
                                    : { color: '#6B7280' }
                            }
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            }
        >
            <div
                className="rounded-xl px-4 py-3 text-sm font-bold text-center mb-5"
                style={overallPassed ? PASS_STYLE : FAIL_STYLE}
            >
                {data.status} - {data.results.overall}
            </div>

            {menu === 'overview' ? renderOverview() : null}
            {menu === 'docs' ? renderDocs() : null}
            {menu === 'face' ? renderFace() : null}
            {menu === 'data' ? renderData() : null}
        </Card>
    );
};

export default VOIApplicantCard;
